import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { BRAND } from '@/config/brand';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const FAQS = [
  {
    q: 'How do I search for homes on the map?',
    a: `Open Map Search from the header or go to /map. Pan and zoom the map, use the search bar for a city or address, and apply filters (price, beds, property type, and more). Results update as you move the map so you can explore neighborhoods visually.`,
  },
  {
    q: 'What is the difference between Buy and Rent filters?',
    a: 'Buy shows for-sale listings (Sale). Rent shows lease/rental listings. Use the Sale / Rent toggle on map or listings pages, or start from the Buy and Rent menus in the header. You can further narrow by property type, such as apartments or single-family homes.',
  },
  {
    q: 'How do I save homes and create an account?',
    a: `Tap the heart on any listing to save it. You will be asked to sign in or create a free ${BRAND.name} account. Saved homes appear under Dashboard → Saved Homes so you can revisit favorites later.`,
  },
  {
    q: 'How do I contact an agent about a listing?',
    a: 'Open a listing detail page and use the contact form or agent details shown there. Share your name, email, phone, and a short message. Agents typically respond during business hours.',
  },
  {
    q: 'How do I list or sell my home?',
    a: 'Sign in and go to Dashboard → My Listings to manage your properties. For pricing tools and advertising options, visit Advertise. To find a seller’s agent, use the Agents directory after signing in.',
  },
  {
    q: 'How does advertising on casahom work?',
    a: 'Sponsored placements help agents and brokerages reach active shoppers on the homepage, listings, and map. See pricing and request a demo on the Advertise page. Sponsored results are clearly labeled.',
  },
  {
    q: 'What should I know about IDX / MLS listing data?',
    a: 'Listing information is provided via IDX for consumers’ personal, non-commercial use. Data is deemed reliable but not guaranteed; buyers should verify all details. Information updates frequently (typically every 15 minutes). Always confirm facts with the listing agent before making decisions.',
  },
  {
    q: 'How is my account and privacy handled?',
    a: `Your account stores profile details, saved homes, and preferences needed to personalize your experience. We do not sell your personal information. For privacy questions or data requests, contact ${BRAND.support.email}.`,
  },
  {
    q: 'Is there a mobile app?',
    a: `${BRAND.name} is fully usable in your mobile browser today. Native iOS and Android apps are coming soon — watch the App Store and Google Play badges in the footer for launch updates.`,
  },
  {
    q: 'How do I contact support?',
    a: BRAND.support.phone
      ? `Email ${BRAND.support.email} or call ${BRAND.support.phone} (${BRAND.hours}). You can also send a message on our Contact page. For accessibility accommodation requests, use Contact as well.`
      : `Email ${BRAND.support.email} (${BRAND.hours}). You can also send a message on our Contact page. For accessibility accommodation requests, use Contact as well.`,
  },
];

export default function Help() {
  const revealRef = useScrollReveal();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQS;
    return FAQS.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
  }, [query]);

  return (
    <div ref={revealRef}>
      <section className="relative hero-gradient noise-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <p className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-white/80">{BRAND.name}</p>
          <h1 className="font-display mx-auto mt-4 max-w-3xl text-4xl font-bold text-white sm:text-5xl">Help Center</h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/85">
            Answers about searching, saving homes, agents, listing data, and your account.
          </p>
          <div className="relative mx-auto mt-8 max-w-lg">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} aria-hidden />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search FAQs…"
              className="rounded-full border-0 bg-white pl-10 shadow-md"
              aria-label="Search frequently asked questions"
            />
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-2xl font-semibold text-slate-900 sm:text-3xl">Frequently asked questions</h2>
        {filtered.length === 0 ? (
          <p className="mt-8 text-sm text-slate-600">No FAQs match “{query}”. Try another term or contact support.</p>
        ) : (
          <Accordion type="multiple" className="mt-8">
            {filtered.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="mb-3 rounded-xl border border-slate-200 bg-white px-4">
                <AccordionTrigger className="text-left text-sm font-medium text-slate-900">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}

        <div className="mt-12 rounded-2xl border border-slate-200 bg-sky-50 p-6 text-center sm:p-8">
          <h3 className="font-display text-xl font-semibold text-slate-900">Still need help?</h3>
          <p className="mt-2 text-sm text-slate-600">Our team is happy to assist with account, listing, or accessibility questions.</p>
          <Button asChild className="mt-5 rounded-full">
            <Link to="/contact">Contact support</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
